import type { Dispatch } from 'redux'

import type { RecipeUUID } from '@library/menu-service'
import type { Box } from '@library/menu-service/queries/menuApiBoxes/types'

import { actionTypes } from 'actions/actionTypes'
import { trackUserAddRemoveRecipe } from 'actions/loggingmanager'
import * as trackingKeys from 'actions/trackingKeys'
import { getIsAuthenticated } from 'selectors/auth'
import {
  getBasketMenuId,
  getBasketMenuUuid,
  getBasketRecipes,
  getNumPortions,
} from 'selectors/basket'
import { getAvailableBoxTypes } from 'selectors/menu-boxes/boxes'
import { getCurrentCollectionId } from 'selectors/menu-collections/getCurrentCollectionId'
import { getAvailableRecipes } from 'selectors/menu-recipes/getAvailableRecipes'
import { basketSum, getBasketRecipeQuantity } from 'utils/basket'

const DEFAULT_MAX_RECIPES = 4

type State = any
type GetState = () => State

const getMaxRecipesForPortion = (state: State, numberOfPortions = 2) => {
  const boxes = getAvailableBoxTypes(state)
  const filteredBoxes = boxes.filter((box: Box) => box.numberOfPortions === numberOfPortions)
  const recipeCounts = filteredBoxes.map((box: Box) => box.numberOfRecipes)

  return recipeCounts.length > 0 ? Math.max(...recipeCounts) : DEFAULT_MAX_RECIPES
}

const getRecipeLimitReached = (state: State) => {
  const recipesInBasket = getBasketRecipes(state)
  const maxRecipesNum = getMaxRecipesForPortion(state, getNumPortions(state))
  const isAuthenticated = getIsAuthenticated(state)

  return !isAuthenticated && basketSum(recipesInBasket) >= maxRecipesNum
}

const removeRecipeFromBasket =
  (id: RecipeUUID) => (dispatch: Dispatch<any>, getState: GetState) => {
    const state = getState()
    const recipesInBasket = getBasketRecipes(state)
    const reachedLimit = getRecipeLimitReached(state)

    const basketRecipesWithoutRecipeId = Object.keys(recipesInBasket.toJS()).filter(
      (key) => key !== id,
    )

    dispatch({
      type: actionTypes.BASKET_RECIPE_REMOVE,
      recipeId: id,
      markUnsaved: true,
      trackingData: {
        actionType: trackingKeys.removeRecipe,
        basketRecipes: basketRecipesWithoutRecipeId,
        boxSize: getNumPortions(state),
        boxType: 'chosen',
        menu_id: getBasketMenuId(state),
        menu_uuid: getBasketMenuUuid(state),
        recipe_uuid: id,
        view: undefined,
        position: undefined,
        collection: getCurrentCollectionId(state),
        basket_recipe_count: recipesInBasket.size - 1,
      },
    })

    if (!reachedLimit) {
      dispatch({
        type: actionTypes.BASKET_LIMIT_REACHED,
        limitReached: reachedLimit,
        trackingData: {
          actionType: trackingKeys.basketLimit,
          limitReached: reachedLimit,
          view: undefined,
          source: actionTypes.RECIPE_REMOVED,
        },
      })
    }

    dispatch(trackUserAddRemoveRecipe())
  }

export const removeMissingRecipesFromBasket =
  () => (dispatch: Dispatch<any>, getState: GetState) => {
    const state = getState()
    const basketRecipes = getBasketRecipes(state)
    const availableRecipes = getAvailableRecipes(state)
    const basketRecipeAmounts = basketRecipes.toJS()

    Object.entries(basketRecipeAmounts).forEach(([uuid, entry]) => {
      if (!(uuid in availableRecipes)) {
        const amount = getBasketRecipeQuantity(entry)
        for (let i = 0; i < amount; i++) {
          dispatch(removeRecipeFromBasket(uuid as RecipeUUID))
        }
      }
    })
  }
